/**
 * OrgResetPassword.jsx
 *
 * Consumes a password-reset token (from email link) and lets the admin set
 * a new password.  URL: /org-reset-password?token=…
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { orgAuthApi } from '../services/api';
import { formatApiDetail } from '../utils/apiError';
import './OrgResetPassword.css';

function EyeIcon({ open }) {
  return open ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'];
  return { score, label: labels[score] || 'Very strong' };
}

export default function OrgResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (!token) setApiError('No reset token found. Please use the link from your email.');
  }, [token]);

  function validate() {
    const e = {};
    if (!password) e.password = 'Password is required.';
    else if (password.length < 8) e.password = 'Must be at least 8 characters.';
    if (password && confirm !== password) e.confirm = 'Passwords do not match.';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    setApiError('');
    try {
      await orgAuthApi.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      const detail = formatApiDetail(err?.response?.data?.detail) || 'Reset failed. The link may be expired or already used.';
      setApiError(detail);
    } finally {
      setSubmitting(false);
    }
  }

  const strength = getPasswordStrength(password);

  return (
    <div className="org-reset__outer">
      <div className="org-reset__card">
        <div className="org-reset__brand">
          <img src="/header-logo.png" alt="Global Mobility" className="org-reset__logo" />
          <span className="org-reset__brand-name">Global Mobility</span>
        </div>

        {done ? (
          <div className="org-reset__success">
            <div className="org-reset__success-icon" aria-hidden>✅</div>
            <h3 className="org-reset__success-title">Password updated</h3>
            <p className="org-reset__success-body">Your password has been changed. You can now log in.</p>
            <button type="button" className="org-reset__btn org-reset__btn--primary" onClick={() => navigate('/login', { replace: true })}>
              Go to login
            </button>
          </div>
        ) : (
          <>
            <h2 className="org-reset__step-title">Set a new password</h2>
            <p className="org-reset__step-hint">Choose a strong password for your admin account.</p>

            {apiError && (
              <div className="org-reset__error" role="alert">{apiError}</div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="org-reset__field">
                <label className="org-reset__label" htmlFor="new-pw">New password</label>
                <div className="org-reset__input-wrap">
                  <input
                    id="new-pw"
                    type={showPw ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={`org-reset__input ${errors.password ? 'error' : ''}`}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrors({}); }}
                    placeholder="Minimum 8 characters"
                  />
                  <button type="button" className="org-reset__eye-btn" onClick={() => setShowPw(v => !v)} aria-label="Toggle visibility">
                    <EyeIcon open={showPw} />
                  </button>
                </div>
                {password && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                      {[1,2,3,4,5].map(n => (
                        <div key={n} style={{
                          height: 4, flex: 1, borderRadius: 2,
                          background: strength.score >= n
                            ? ['#ef4444','#f97316','#eab308','#22c55e','#10b981'][Math.min(strength.score,5)-1]
                            : '#e2e8f0',
                          transition: 'background 0.3s',
                        }} />
                      ))}
                    </div>
                    <span style={{ fontSize: 12, color: '#6b7280', minWidth: 60, textAlign: 'right' }}>{strength.label}</span>
                  </div>
                )}
                {errors.password && <span className="org-reset__field-error">{errors.password}</span>}
              </div>

              <div className="org-reset__field">
                <label className="org-reset__label" htmlFor="confirm-pw">Confirm password</label>
                <div className="org-reset__input-wrap">
                  <input
                    id="confirm-pw"
                    type={showConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={`org-reset__input ${errors.confirm ? 'error' : ''}`}
                    value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); setErrors({}); }}
                    placeholder="Repeat your password"
                  />
                  <button type="button" className="org-reset__eye-btn" onClick={() => setShowConfirm(v => !v)} aria-label="Toggle visibility">
                    <EyeIcon open={showConfirm} />
                  </button>
                </div>
                {errors.confirm && <span className="org-reset__field-error">{errors.confirm}</span>}
              </div>

              <button type="submit" className="org-reset__btn org-reset__btn--primary" disabled={submitting || !token}>
                {submitting ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
